/**
 * @typedef {"cluster" | "primary_marker"  | "country" | "user" | "defined" | "default"} MapFocus
 */

/**
 * @typedef {Object} IMapProps
 * @property {MapFocus=} preferredMapFocus
 * @property {MapFocus[]=} preferredMapFocusOrder
 * @property {CountryCode=} countryCode
 * @property {(() => void)=} onMapChange
 * @property {(() => void)=} onMapInteraction
 * @property {((marker:OSM.MapMarker) => void)=} onMarkerSelected
 * @property {maplibregl.LngLatLike=} definedFocus
 * @property {maplibregl.LngLatBoundsLike=} definedFocusBounds
 * @property {boolean=} allowMapContainerFocusScroll
 * @property {maplibregl.MapOptions=} mapOptions
 * @property {IBasicMarkerProps=} userMarker
 * @property {IMarkerProps=} primaryMarker
 * @property {IMapClusterProps=} cluster
 * @property {IMapPopupProps=} popup
 * @property {boolean=} singleMarkerIsPrimaryMarker
 * @property {maplibregl.GeolocateControlOptions & {position: maplibregl.ControlPosition=}=} geolocateControl
 * @property {maplibregl.FullscreenControlOptions & {position: maplibregl.ControlPosition=}=} fullscreenControl
 * @property {(maplibregl.NavigationControlOptions & {position: maplibregl.ControlPosition=})|null=} navigationControl
 * @property {maplibregl.ScaleControlOptions & {position: maplibregl.ControlPosition=}=} scaleControl
 * @property {boolean=} enableContextMenu
 * @property {number=} limitFlyToCurve
 * @property {maplibregl.PropertyValueSpecification<maplibregl.ProjectionDefinitionSpecification>=} projectionType
 * @property {((event:maplibregl.MapMouseEvent & Object) => void)=} onMapClick
 */

/**
 * @typedef {Object.<string, IMapProps>} IMapsProps
 */

/**
 * @typedef {Object.<string, MapLibreMap>} IMapLibreMaps
 */

window.OSM = window.OSM ?? {};
/** @type {IMapLibreMaps} */
OSM.Maps = {};
/** @type {MapLibreMap|null} */
OSM.DafaultMap = null;
OSM.MapLibreMap = class MapLibreMap {

    /** @type {string} */
    mapID;
    /** @type {IMapProps} */
    props;

    /** @type {MapFocus[]} */
    preferredMapFocusOrder = [];

    /** @type {MapFocus} */
    preferredMapFocus = "default";

    /** @type {{didRequest:boolean=, didReceive:boolean=, position:maplibregl.LngLat|null=, countryCode:CountryCode|null= }} */
    userLocation = {}

    /** @type {{didRequest:boolean=, didReceive:boolean=, countryCode:CountryCode|null=, bounds:maplibregl.LngLatBounds|null=, position:maplibregl.LngLat|null= }} */
    countryLocation  = {}

    isMapReady = false;
    styleLoaded = false;

    /** @type {HTMLElement} */
    outerContainer;

    /** @type {maplibregl.Map} */
    map;

    /** @type {OSM.MapMarker|null} */
    primaryMarker = null;

    /** @type {OSM.MapCluster|null} */
    cluster = null;

    /** @type {OSM.MapPopup|null} */
    popup = null;

    previousZoom = 0;

    previousLngLat = new maplibregl.LngLat(0, 0);

    /** @type {OSM.MapMarker|null} */
    selectedMarker = null;

    showSelectedMarkerPopup = false;

    /** @type {maplibregl.MapOptions} */
    mapOptions;

    /** @type {IMarkerProps|null} */
    userMarkerProps = null;

    /** @type {OSM.MapMarker|null} */
    userMarker = null;

    /** @type {IMarkerProps|null} */
    primaryMarkerProps = null;

    /** @type {maplibregl.LngLat} */
    definedFocus = null;

    /** @type {maplibregl.LngLatBounds|null} */
    definedFocusBounds = null;

    didFindActiveFocus = false;

    mapInteractionCooldown = new OSM.CooldownHandler(true);

    mapChangeCooldown = new OSM.CooldownHandler(true);

    /** @type {HTMLElement|null} */
    transitionOverlay = null;

    /** @type {IMapProps} */
    coreConfig

    /**
     *
     * @param {string} mapID
     * @param {IMapProps} props
     */
    constructor(mapID, props) {

        this.mapID = mapID;
        this.props = props ?? {};
        this.coreConfig = OSM.MapConfigs.core ?? {};
        this.initialize()
        OSM.Maps[this.mapID] = this;
        if(!OSM.DafaultMap){
            OSM.DafaultMap = this;
        }
    }

    /**
     *
     * @returns {void}
     */
    initialize(){

        /** @type {HTMLElement} */
        const outerContainer = document.querySelectorAll('#'+this.mapID)[0] ?? null;
        if(!outerContainer){
            return
        }
        this.outerContainer = outerContainer;

        this.bindFunctions()
        this.applyProps();
        this.initMap();
    }

    /**
     *
     * @returns {void}
     */
    bindFunctions()
    {
        this.onMapStyleLoad = this.onMapStyleLoad.bind(this);
        this.onMapClick = this.onMapClick.bind(this);
        this.onMapZoomEnd = this.onMapZoomEnd.bind(this);
        this.onMapMove = this.onMapMove.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
    }

    /**
     *
     * @returns {void}
     */
    applyProps(){
        const props = this.props;

        const fromMapOptions = props.mapOptions ?? null;
        const configMapOptions = this.coreConfig.mapOptions ?? null;

        const style = this.outerContainer.style;
        const width100 = !style.width && (!style.height || !style.aspectRatio);
        const useDefaultAspectRatio = !(style.width && !width100) || !style.height;
        style.width = !width100 ? style.width  : "100%";
        style.aspectRatio = !useDefaultAspectRatio ? style.aspectRatio  : 16/9;

        const projectionType = this.props?.projectionType ?? this.coreConfig?.projectionType;

        this.mapOptions = {
            ...this.coreConfig.mapOptions,
            ...fromMapOptions,
            container: this.mapID,
            style: fromMapOptions?.style ?? configMapOptions?.style,
            center: maplibregl.LngLat.convert(fromMapOptions?.center ?? (configMapOptions?.center ?? [0,0])),
            zoom: fromMapOptions?.zoom ?? configMapOptions?.zoom,
            attributionControl: fromMapOptions?.attributionControl ?? configMapOptions?.attributionControl,
            dragRotate: fromMapOptions?.dragRotate ?? (configMapOptions?.dragRotate ?? (!projectionType ? false : undefined)),
        };

        const definedFocus = this.props.definedFocus ?? (this.coreConfig?.definedFocus ?? null)
        this.definedFocus = definedFocus ? maplibregl.LngLat.convert(definedFocus) : this.mapOptions.center;

        const definedFocusBounds = this.props.definedFocusBounds ?? (this.coreConfig?.definedFocusBounds ?? null)
        this.definedFocusBounds = definedFocusBounds ? maplibregl.LngLatBounds.convert(definedFocusBounds) : null;

        this.applyUserMarker(props.userMarker ?? null);
        this.applyPrimaryMarker(props.primaryMarker ?? null);
        this.applyCluster(props.cluster ?? null);
        if(props.countryCode){
            this.requestCountryLocation(props.countryCode).then(

                /**
                 *
                 * @param _ {maplibregl.LngLat|null}
                 */
                (_) => {
                    this.checkMapReady();
                });
        }
        this.preferredMapFocusOrder = !!(props.preferredMapFocusOrder ?? []).length ? props.preferredMapFocusOrder : (this.coreConfig.preferredMapFocusOrder ?? []);
        this.preferredMapFocus = props.preferredMapFocus ?? (this.preferredMapFocusOrder[0] ?? "default");
    }

    /**
     *
     * @param {IMarkerProps|null} primaryMarkerProps
     */
    applyPrimaryMarker(primaryMarkerProps)
    {
        if(!primaryMarkerProps){
            this.primaryMarkerProps = null;
            if(this.primaryMarker){
                this.primaryMarker.removeFromMap();
            }
            this.primaryMarker = null;
        } else {
            /** @type {IMarkerProps} */
            const fromProps = primaryMarkerProps;
            const fromConfigProps = this.coreConfig.primaryMarker;

            let defaultZoomLevel = fromProps.defaultZoomLevel ?? null;
            const fallbackToBoundsZoomLevel= fromProps.fallbackToBoundsZoomLevel ?? (fromConfigProps?.fallbackToBoundsZoomLevel ?? false);
            if(!fallbackToBoundsZoomLevel){
                defaultZoomLevel = defaultZoomLevel ?? (fromConfigProps?.defaultZoomLevel ?? (this.mapOptions.zoom ?? null));
            }

            const iconProps = {
                ...fromConfigProps.iconProps,
                ...fromProps.iconProps,
                imagePath: fromProps.iconProps?.imagePath !== undefined ? fromProps.iconProps?.imagePath : fromConfigProps?.iconProps?.imagePath,
            }
            this.primaryMarkerProps = {
                ...fromConfigProps,
                ...fromProps,
                iconProps: iconProps,
                centreOnClick: fromProps?.centreOnClick ?? (fromConfigProps?.centreOnClick ?? false),
                defaultZoomLevel: defaultZoomLevel
            }
            if(this.primaryMarker){
                this.primaryMarker.removeFromMap();
            }
            this.primaryMarker = new OSM.MapMarker(this, "primary_marker", this.primaryMarkerProps)
            if(this.map){
                this.primaryMarker.addToMap();
            }
        }
    }

    /**
     *
     * @param {IMapClusterProps|null} clusterProps
     */
    applyCluster(clusterProps)
    {
        if(this.cluster){
            this.cluster.removeFromMap();
        }
        if(!clusterProps){
            return;
        }
        this.cluster = new OSM.MapCluster(this, clusterProps);
    }

    /**
     *
     * @returns {void}
     */
    initMap(){

        const map = new maplibregl.Map(this.mapOptions);
        this.map = map;
        map.on('style.load', this.onMapStyleLoad);
        map.on('click', this.onMapClick);


        if(this.props.geolocateControl){
            /** @type {maplibregl.GeolocateControlOptions & {position: maplibregl.ControlPosition=}} */
            const options = {
                ...this.coreConfig.geolocateControl,
                ...this.props.geolocateControl
            };
            const position = options.position;
            delete options.position;
            map.addControl(new maplibregl.GeolocateControl(options), position);
        }
        if(this.props.fullscreenControl){
            /** @type {maplibregl.FullscreenControlOptions & {position: maplibregl.ControlPosition=}} */
            const options = {
                ...this.coreConfig.fullscreenControl,
                ...this.props.fullscreenControl
            };
            const position = options.position;
            delete options.position;
            map.addControl(new maplibregl.FullscreenControl(options), position);
        }
        if(this.props.navigationControl !== null){
            /** @type {maplibregl.NavigationControlOptions & {position: maplibregl.ControlPosition=}} */
            const options = {
                ...this.coreConfig.navigationControl,
                ...this.props.navigationControl
            };
            const position = options.position;
            delete options.position;
            map.addControl(new maplibregl.NavigationControl(options), position);
        }
        if(this.props.scaleControl){
            /** @type {maplibregl.ScaleControlOptions & {position: maplibregl.ControlPosition=}} */
            const options = {
                ...this.coreConfig.scaleControl,
                ...this.props.scaleControl
            };
            const position = options.position;
            delete options.position;
            map.addControl(new maplibregl.ScaleControl(options), position);
        }

        if(this.userMarker){
            this.userMarker.addToMap();
        }
        if(this.primaryMarker){
            this.primaryMarker.addToMap();
        }

    }

    /**
     *
     * @param {maplibregl.Event & {style:maplibregl.Style}} _
     */
    onMapStyleLoad = (_) => {
        this.styleLoaded = true;
        this.checkMapReady()
    }

    /**
     *
     * @param {maplibregl.MapMouseEvent & Object} event
     */
    onMapClick = (event) => {
        if(!this.isMapReady){
            return;
        }

        const doubleClickZoom = this.mapOptions.doubleClickZoom !== false ? (this.map.doubleClickZoom ?? null) : null;
        /** @type {MouseEvent & {clickOnLayer:boolean=}} */
        const customEvent = event.originalEvent;
        if(customEvent.clickOnLayer){
            if(doubleClickZoom){
                doubleClickZoom.disable();
            }
            return;
        } else {
            if(doubleClickZoom){
                doubleClickZoom.enable();
            }
        }

        if(this.selectedMarker && (this.popup?.props.closeOnMapClick ?? false)){
            this.deselectSelectedMarker();
        }
        if(this.props.onMapClick){
            this.props.onMapClick(event)
        }
    }

    /**
     *
     * @param {maplibregl.MapMouseEvent|maplibregl.MapTouchEvent|maplibregl.MapWheelEvent} _
     */
    onMapZoomEnd = (_) => {
        const zoom = this.map.getZoom() ?? 0;
        if (zoom !== this.previousZoom)
        {
            this.previousZoom = zoom;
            this.handleMapChange()
        }
    }

    /**
     *
     * @param {maplibregl.MapMouseEvent|maplibregl.MapTouchEvent|maplibregl.MapWheelEvent} _
     */
    onMapMove = (_) => {
        const boundsCentre = this.map.getBounds().getCenter();
        if (this.isNear(boundsCentre, this.previousLngLat))
        {
            return;
        }
        this.previousLngLat.lat = boundsCentre.lat;
        this.previousLngLat.lng = boundsCentre.lng;
        this.handleMapInteraction()
    }

    /**
     *
     * @param event {KeyboardEvent}
     */
    onKeyDown(event){
        if (event.key === 'Escape') {
            this.onEscapePressed();
        }
    }

    onEscapePressed(){

        if(this.selectedMarker && (this.popup?.props.closeOnEscape ?? false)){
            this.deselectSelectedMarker();
        }
    }

    /**
     *
     * @returns {void}
     */
    checkMapReady(){
        if(this.isMapReady){
            return;
        }
        if(!this.styleLoaded){
            return;
        }
        if(!this.haveAllExternalData()){
            return;
        }
        this.checkForFindActiveFocus();
        if(this.cluster){
            if(!this.cluster.appliedToMap){
                this.cluster.applyToMap();
                return;
            }
            if(this.cluster.waitingForSourceData){
                return;
            }
        }
        this.isMapReady = true;

        this.map.on('zoomend', this.onMapZoomEnd);
        this.map.on('move', this.onMapMove);
        document.addEventListener("keydown", this.onKeyDown);


        const projectionType = this.props?.projectionType ?? this.coreConfig?.projectionType;
        if(projectionType){

            this.map.setProjection({
                type: projectionType
            })
        }

        if (!this.props.allowMapContainerFocusScroll)
        {
            this.map.getContainer().focus({preventScroll: true});
        }

        const enableContextMenu = this.props.enableContextMenu ?? (this.coreConfig.enableContextMenu ?? false);
        if(enableContextMenu){
            this.map?._canvas.addEventListener("contextmenu", (event) => {
                event.stopImmediatePropagation();
            })
            const buttons = this.outerContainer.getElementsByTagName("button");
            for(let button of buttons){
                button.addEventListener("contextmenu", (event) => {
                    event.stopImmediatePropagation();
                })
            }
        }
        this.outerContainer.classList.add("is-initialized");
        if(this.showSelectedMarkerPopup){
            this.openPopup();
        }

        document.dispatchEvent(new CustomEvent("site.osm-ready", {detail: {map:this, mapID:this.mapID, isDefault:this === OSM.DafaultMap}}));
    }

    /**
     *
     * @returns {boolean}
     */
    haveAllExternalData() {
        if(this.userMarkerProps){
            if(!this.props.userMarker?.lngLat){
                if(!this.userLocation.didReceive){
                    return false;
                }
            }
        }
        if(this.props.countryCode){
            if(!this.countryLocation.didReceive){
                return false;
            }
        }
        return true;
    }

    /**
     *
     * @param {IBasicMarkerProps|null} userMarkerProps
     * @param {boolean=} requestLocationAgain
     */
    applyUserMarker(userMarkerProps, requestLocationAgain)
    {
        if(!userMarkerProps){
            this.userMarkerProps = null;
            if(this.userMarker){
                this.userMarker.removeFromMap();
            }
            this.userMarker = null;
        } else {
            /** @type {IBasicMarkerProps} */
            const fromProps = userMarkerProps;
            const fromConfigProps = this.coreConfig.userMarker;

            let defaultZoomLevel = fromProps.defaultZoomLevel ?? null;
            const fallbackToBoundsZoomLevel= fromProps.fallbackToBoundsZoomLevel ?? (fromConfigProps?.fallbackToBoundsZoomLevel ?? false);
            if(!fallbackToBoundsZoomLevel){
                defaultZoomLevel = defaultZoomLevel ?? (fromConfigProps?.defaultZoomLevel ?? (this.mapOptions.zoom ?? null));
            }

            const iconProps = {
                ...fromConfigProps.iconProps,
                ...fromProps.iconProps,
                imagePath: fromProps.iconProps?.imagePath !== undefined ? fromProps.iconProps?.imagePath : fromConfigProps?.iconProps?.imagePath
            }
            /** @type {maplibregl.LngLatLike|null} */
            const previousLngLat = this.userMarkerProps?.lngLat ?? null;
            this.userMarkerProps = {
                ...fromConfigProps,
                ...fromProps,
                lngLat: fromProps.lngLat ?? [0, 0],
                iconProps: iconProps,
                centreOnClick: fromProps?.centreOnClick ?? (fromConfigProps?.centreOnClick ?? false),
                defaultZoomLevel: defaultZoomLevel,
                popupContent: fromProps?.popupContent !== null ? (this.userMarkerProps?.popupContent ?? fromConfigProps?.popupContent) : null
            }
            if(!this.userMarkerProps.lngLat && !!previousLngLat && this.userLocation.didReceive && !requestLocationAgain){
                this.userMarkerProps.lngLat = previousLngLat;
            }

            const addMarker = () => {

                this.userMarker = new OSM.MapMarker(this, "user", this.userMarkerProps)
                if(this.map){
                    this.userMarker.addToMap();
                }
            }
            if(!!fromProps.lngLat){
                this.userLocation.didRequest = false;
                this.userLocation.didReceive = false;
                if(this.userMarker){
                    this.userMarker.removeFromMap();
                }
                addMarker();
            } else {
                this.requestUserLocation().then(
                    /**
                     *
                     * @param position {maplibregl.LngLat|null}
                     */
                    (position) => {
                        if(this.userMarker){
                            this.userMarker.removeFromMap();
                        }
                        if(position){
                            this.userMarkerProps.lngLat = position;
                            addMarker();
                        }
                        this.checkMapReady();
                    })
            }
        }
    }

    /**
     *
     * @returns {Promise<maplibregl.LngLat|null>}
     */
    async requestUserLocation()
    {
        this.userLocation = {
            didRequest: true,
            didReceive: false
        }

        const locationManager = OSM.manager.getLocationManager();
        return OSM.manager.getLocationManager().requireUserLocation(true).then(() => {
            if(!this.userLocation.didRequest){
                return null;
            }
            const position = locationManager.getLastKnowLngLat();
            /** @type {CountryCode|null} */
            const countryCode = position ? (locationManager.lastKnownUserAddress?.countryCode ? locationManager.lastKnownUserAddress.countryCode : null) : null;
            this.userLocation.didReceive = true;
            this.userLocation.position = position;
            this.userLocation.countryCode = countryCode;
            return position;
        })
    }

    /**
     *
     * @param countryCode {CountryCode}
     * @returns {Promise<maplibregl.LngLatBounds|null>}
     */
    requestCountryLocation(countryCode)
    {
        this.countryLocation = {
            didRequest: true,
            didReceive: false,
            countryCode: countryCode
        }

        const locationManager = OSM.manager.getLocationManager();

        return locationManager.getCountryLocation(countryCode).then(
            /**
             *
             * @param itemAddress {ILocationAddress|null}
             */
            (itemAddress) => {
                if((this.countryLocation.countryCode !== countryCode) || !this.countryLocation.didRequest){
                    return null;
                }
                this.countryLocation.didReceive = true;
                this.countryLocation.bounds = itemAddress?.lngLatBounds;
                this.countryLocation.position = itemAddress?.lngLat;
                return this.countryLocation.bounds
            })
    }

    /**
     *
     * @returns {boolean}
     */
    checkForFindActiveFocus()
    {
        if(this.didFindActiveFocus && this.didFindInitialFocus){
            return false;
        }
        const mapFocus = this.getActiveMapFocus();
        const strictMapFocus = this.getActiveMapFocus(true);
        if(mapFocus !== strictMapFocus){
            return false;
        }
        this.didFindActiveFocus = true;
        this.didFindInitialFocus = true;
        const selectedMarkerFocus = this.getSelectedMarkerFocus();
        if(selectedMarkerFocus && selectedMarkerFocus !== mapFocus){
            this.popup?.closePopup();
        }
        if(this.isMapReady){
            this.flyToFocus();
        } else {
            this.jumpToFocus();
        }
        return true;
    }

    /**
     *
     * @param {OSM.MapMarker} _
     */
    onMarkerPositionChange(_)
    {
        this.handleMapChange();
    }

    /**
     *
     * @returns {void}
     */
    handleMapChange()
    {
        this.handleMapInteraction();
        this.mapChangeCooldown.available().then(() => {
            if(this.props.onMapChange)
            {
                this.props.onMapChange();
            }
        })
    }

    /**
     *
     * @returns {void}
     */
    handleMapInteraction()
    {
        this.mapInteractionCooldown.available().then(() => {
            if(this.props.onMapInteraction)
            {
                this.props.onMapInteraction();
            }
        })
    }

    /**
     *
     * @returns {MapFocus|null}
     */
    getSelectedMarkerFocus()
    {
        const selectedMarker = this.selectedMarker;
        if(!selectedMarker){
            return null;
        }
        if(selectedMarker === this.userMarker){
            return "user";
        }
        if(selectedMarker === this.userMarker){
            return "primary_marker";
        }
        const markerID = selectedMarker.props.markerID ?? null;
        const cluster = this.cluster;
        if(cluster && markerID && cluster.clusterObjects[markerID]){
            return "cluster";
        }
        return null;
    }

    /**
     *
     * @returns {void}
     */
    resetPrimaryMarkerPosition()
    {
        if(!this.primaryMarker || !this.props.primaryMarker?.lngLat){
            return;
        }
        this.primaryMarker.setLngLat(this.props.primaryMarker.lngLat);
        this.didFindActiveFocus = false;
        this.checkForFindActiveFocus();
    }

    showTransition(){
        if(this.transitionOverlay){
            this.transitionOverlay.remove();
        }
        const transitionOverlay = document.createElement("div");
        transitionOverlay.classList.add("transition-overlay");
        this.outerContainer.appendChild(transitionOverlay);
        this.transitionOverlay = transitionOverlay
        setTimeout(() => {
            transitionOverlay.classList.add("transition");
        })
    }

    /**
     *
     * @param strict {boolean=}
     * @returns {MapFocus}
     */
    getActiveMapFocus(strict)
    {
        const preferredMapFocus = this.preferredMapFocus;
        const preferredOrder = this.preferredMapFocusOrder.slice();

        const index = preferredOrder.indexOf(preferredMapFocus);
        if(index >= 0){
            preferredOrder.splice(index, 1);
            preferredOrder.unshift(preferredMapFocus);
        }
        /** @type {MapFocus|null} */
        let validFocus = null;
        for (let mapFocus of preferredOrder)
        {
            switch (mapFocus)
            {
                case "defined":
                    validFocus = mapFocus;
                    break;
                case "primary_marker":
                    validFocus = !!this.primaryMarker || (!strict && !!this.primaryMarkerProps) ? mapFocus : null;
                    break;
                case "user":
                    validFocus = !!this.userMarker || (!strict && (!!this.props.userMarker?.lngLat || (!!this.userLocation.didRequest && !this.userLocation.didReceive))) ? mapFocus : null;
                    break;
                case "country":
                    validFocus = !!this.countryLocation.bounds || (!strict && !!this.countryLocation.didRequest && !this.countryLocation.didReceive) ? mapFocus : null;
                    break;
                case "cluster":
                    validFocus = !!this.getClusterBounds() ? mapFocus : null;
                    break;
                case "default":
                    validFocus = mapFocus;
                    break;
                default:
                    break;
            }
            if(validFocus){
                break;
            }
        }
        return validFocus ?? "default";
    }

    requirePopupInFocus()
    {
        const markerLngLat = this.selectedMarker?.getLngLat() ?? null;
        if(!this.selectedMarker?.popupContent || !this.showSelectedMarkerPopup || !markerLngLat || !this.popup){
            return;
        }

        if(this.popup.willPopupFit(markerLngLat)){
            return;
        }
        this.flyToFocus();

    }

    /**
     *
     * @param options {maplibregl.FlyToOptions=}
     */
    flyToFocus(options)
    {
        options = {
            ...this.applyFocusToCamera(options)
        }

        const limitFlyToCurve = this.props.limitFlyToCurve ?? (this.coreConfig.limitFlyToCurve ?? 0);
        if(limitFlyToCurve > 0){
            const currentZoom = this.map.getZoom();
            const targetZoom = options.zoom ?? currentZoom;
            const targetCentre = ((options.center ?? null) !== null) ? maplibregl.LngLat.convert(options.center) : null;
            if((targetZoom === currentZoom) && targetCentre){
                const currentCenter = this.map.getCenter();
                const bounds = this.map.getBounds();
                const locationManager = OSM.manager.getLocationManager();
                const distanceInMetres = locationManager.getDistanceInMetresBetween(targetCentre, currentCenter);
                const boundsDistanceInMetres = locationManager.getDistanceInMetresBetween(bounds.getNorthWest(), bounds.getSouthEast());
                if(distanceInMetres < (boundsDistanceInMetres * limitFlyToCurve)){
                    options.curve = options.curve ?? 0;
                }
            }
        }
        this.map.flyTo(this.applyFocusToCamera(options))
    }

    /**
     *
     * @param options {maplibregl.JumpToOptions=}
     */
    jumpToFocus(options)
    {
        this.map.jumpTo(this.applyFocusToCamera(options))
    }

    /**
     *
     * @param options {maplibregl.CameraOptions=}
     * @returns {maplibregl.CameraOptions}
     */
    applyFocusToCamera(options)
    {
        options = options ?? {};
        const map = this.map;

        /** @type {maplibregl.LngLat} */
        let position;
        /** @type {number} */
        let zoomLevel;
        const markerLngLat = this.selectedMarker?.getLngLat() ?? null;
        if (markerLngLat)
        {
            position = markerLngLat;
            zoomLevel = map.getZoom()

            /** @type {maplibregl.Point} */
            let focusOffset = null
            if(this.showSelectedMarkerPopup){
                focusOffset = this.popup?.focusOffset ?? null;
            }
            if(!focusOffset){
                focusOffset = this.selectedMarker?.focusOffset ?? null
            }
            if(focusOffset){
                position = this.getPositionWithOffset(position, focusOffset)
            }
        } else
        {
            position = this.getActiveFocusPosition();
            zoomLevel = this.getActiveFocusZoomLevel();
        }

        options = options ?? {};
        options.center = options.center ?? position;
        options.zoom = options.zoom ?? zoomLevel;
        return options;
    }

    /**
     *
     * @param fromPosition {maplibregl.LngLat}
     * @param offset {maplibregl.Point}
     * @returns {maplibregl.LngLat}
     */
    getPositionWithOffset(fromPosition, offset){
        const map = this.map;
        const bounds = map.getBounds();
        const width = bounds.getEast() - bounds.getWest();
        const height = bounds.getNorth() - bounds.getSouth();
        let longitude = fromPosition.lng + (width * -offset.x);
        while(longitude <= -180){
            longitude += 180;
        }
        while(longitude > 180){
            longitude -= 180;
        }
        const latitude = Math.max(Math.min(fromPosition.lat + (height * offset.y), 90), -90);
        return new maplibregl.LngLat(longitude, latitude);
    }

    /**
     *
     * @param marker {MapMarker}
     * @param zoomLevel {number=}
     * @param adjustBy {number=}
     */
    zoomToMarker(marker, zoomLevel, adjustBy)
    {
        zoomLevel = zoomLevel ?? null;
        adjustBy = adjustBy ?? 1;
        if(zoomLevel === null){
            zoomLevel = this.map.getZoom() + adjustBy;
        }
        this.map.flyTo({
            center: marker.getLngLat(),
            zoom: this.normalizeZoomLevel(zoomLevel)
        });
    }

    /**
     *
     * @returns {maplibregl.LngLat}
     */
    getActiveFocusPosition()
    {
        return this.getFocusPosition(this.getActiveMapFocus()) ?? new maplibregl.LngLat(0, 0);
    }

    /**
     *
     * @returns {number}
     */
    getActiveFocusZoomLevel()
    {
        let focus = this.getActiveMapFocus();
        let focusZoomLevel = this.getFocusZoomLevel(focus) ?? 0;

        if (!focusZoomLevel)
        {
            for (let altFocus of this.preferredMapFocusOrder)
            {
                if (altFocus === focus)
                {
                    continue;
                }
                focusZoomLevel = this.getFocusZoomLevel(altFocus) ?? 0;
                if (focusZoomLevel)
                {
                    focus = altFocus;
                    break;
                }
            }
        }
        if (focus === "cluster")
        {

            if (this.cluster)
            {
                const primaryMarkerZoomLevel = this.getFocusZoomLevel("primary_marker");
                const countryZoomLevel = this.getFocusZoomLevel("country");
                const maximumZoomLevel = this.props.cluster?.source?.clusterMaxZoom ?? (this.coreConfig.cluster?.source?.clusterMaxZoom ?? 20);
                focusZoomLevel = this.cluster.getActiveFocusZoomLevel(Math.min(focusZoomLevel, maximumZoomLevel), primaryMarkerZoomLevel, countryZoomLevel)
            }
        }
        return focusZoomLevel;
    }

    /**
     *
     * @param mapFocus {MapFocus}
     * @returns {maplibregl.LngLat}
     */
    getFocusPosition(mapFocus)
    {
        /** @type {maplibregl.LngLat|null} */
        let position = null;
        switch (mapFocus)
        {
            case "primary_marker":
                position = this.primaryMarker?.getLngLat() ?? null;
                break;
            case "user":
                position = this.userMarker?.getLngLat() ?? null;
                break;
            case "defined":
                if(this.definedFocus && !this.definedFocusBounds){
                    position = this.definedFocus;
                }
                break;
            case "default":
                const latitude = this.mapOptions.center?.lat ?? null;
                const longitude = this.mapOptions.center?.lng ?? null;
                if(latitude!== null || longitude !== null){
                    position = new maplibregl.LngLat(longitude ?? 0, latitude ?? 0);
                }
                break;
            default:
                break;
        }
        return position ?? (this.getFocusBounds(mapFocus)?.getCenter() ?? null);
    }

    /**
     *
     * @param mapFocus
     * @returns {maplibregl.LngLatBounds|null}
     */
    getFocusBounds(mapFocus)
    {
        switch (mapFocus)
        {
            case "defined":
                return this.definedFocusBounds ?? null;
            case "user":
                return this.userMarker?.appliedBounds() ?? null;
            case "primary_marker":
                return this.primaryMarker?.appliedBounds() ?? null;
            case "country":
                return this.countryLocation.bounds ?? null;
            case "cluster":
                return this.getClusterBounds();
            default:
                return null;
        }
    }

    /**
     *
     * @returns {maplibregl.LngLatBounds|null}
     */
    getClusterBounds()
    {
        if (!this.cluster)
        {
            return null;
        }
        const userPosition = this.getFocusPosition("user");
        const primaryMarkerPosition = this.getFocusPosition("primary_marker");
        const countryBounds = this.countryLocation.bounds ?? null;
        return this.cluster.getClusterBounds(userPosition, primaryMarkerPosition, countryBounds, this.userLocation.countryCode)
    }

    /**
     *
     * @param mapFocus {MapFocus}
     * @returns {number|null}
     */
    getFocusZoomLevel(mapFocus)
    {
        /** @type {number|null} */
        let zoomLevel = null;
        if(mapFocus === "user"){
            zoomLevel = this.userMarker?.defaultZoomLevel ?? null;
        } else if(mapFocus === "primary_marker"){
            zoomLevel = this.primaryMarker?.defaultZoomLevel ?? null;
        }
        if(zoomLevel === null){
            const bounds = this.getFocusBounds(mapFocus);
            zoomLevel = bounds ? this.getZoomLevelForBounds(bounds) : null;
        }

        if(zoomLevel === null){
            if((mapFocus === "default") || ((mapFocus === "defined") && !this.definedFocusBounds)){
                zoomLevel = this.mapOptions.zoom ?? null;
            }
        }
        if(zoomLevel === null){
            return null;
        }
        return this.normalizeZoomLevel(zoomLevel)
    }

    /**
     *
     * @param bounds {maplibregl.LngLatBounds}
     * @param normalize {boolean=}
     * @returns {number|null}
     */
    getZoomLevelForBounds(bounds, normalize)
    {
        normalize = !!normalize;
        let zoomLevel =  this.map.cameraForBounds(bounds)?.zoom ?? null;
        if(zoomLevel === null || !normalize){
            return zoomLevel;
        }
        return this.normalizeZoomLevel(zoomLevel)
    }

    /**
     *
     * @param zoomLevel {number}
     * @returns {number}
     */
    normalizeZoomLevel(zoomLevel)
    {
        return Math.max(Math.min(Math.floor(zoomLevel), 20), 2)
    }

    /**
     *
     * @param focus {MapFocus}
     * @returns {boolean}
     */
    isInFocus(focus)
    {
        const activeFocus = this.getActiveMapFocus();
        if (activeFocus === focus)
        {
            return true;
        }

        if (activeFocus === "cluster")
        {
            if (this.cluster)
            {
                return this.cluster.containsFocus(focus);
            }
        }
        return false;
    }

    /**
     *
     * @returns {boolean}
     */
    isPrimaryMarkerAtDefaultPosition()
    {
        if (!this.primaryMarker)
        {
            return true;
        }
        const primaryMarkerFocusZoomLevel = this.getFocusZoomLevel("primary_marker");
        return this.primaryMarker.isAtDefaultPosition() && (this.map.getZoom() === (primaryMarkerFocusZoomLevel ?? this.getActiveFocusZoomLevel()));
    }

    /**
     *
     * @returns {boolean}
     */
    isMapAtDefaultPosition()
    {
        const position = this.getActiveFocusPosition();
        if (!position)
        {
            return true;
        }

        return (this.isNear(position, this.map.getBounds().getCenter())) && (this.map.getZoom() === this.getActiveFocusZoomLevel());
    }

    /**
     *
     * @param lngLat1 {maplibregl.LngLat}
     * @param lngLat2 {maplibregl.LngLat}
     * @returns {boolean}
     */
    isNear(lngLat1, lngLat2)
    {
        const latProp = (lngLat1.lat - lngLat2.lat) / (this.map.getBounds().getNorth() - this.map.getBounds().getSouth());
        const lngProp = (lngLat1.lng - lngLat2.lng) / (this.map.getBounds().getEast() - this.map.getBounds().getWest());
        return (latProp < 0.01) && (lngProp < 0.01) && (this.map.getZoom() === this.getActiveFocusZoomLevel());
    }

    /**
     *
     * @returns {void}
     */
    resetMapPosition()
    {
        this.popup?.closePopup();
        this.flyToFocus();
    }

    /**
     *
     * @param markerID {string}
     * @param objectID {string|null=}
     * @param withMove {boolean=}
     * @param withPopup {boolean=}
     * @param onlyExcludeFromClusterIfNecessary {boolean=}
     */
    selectClusterMarker(markerID, objectID, withMove, withPopup, onlyExcludeFromClusterIfNecessary)
    {
        objectID = objectID ?? null;
        withMove = !!withMove;
        withPopup = !!withPopup;

        if(!this.cluster)
        {
            this.selectMarker(null);
            return;
        }
        this.cluster.excludeMarkerFromCluster(markerID, objectID, onlyExcludeFromClusterIfNecessary).then(
            /**
             *
             * @param marker {OSM.MapMarker|null}
             */
            (marker) => {

                if(!marker){
                    this.selectMarker(null);
                    return;
                }
                this.selectMarker(marker, withMove, withPopup);
            })
    }

    /**
     *
     * @param {OSM.MapMarker} marker
     * @param {boolean=} withMove
     * @param {boolean=} withPopup
     */
    selectMarker(marker, withMove, withPopup)
    {
        withMove = !!withMove;
        withPopup = !!withPopup;
        if(!marker){
            if(this.selectedMarker){
                this.deselectSelectedMarker();
            }
            return;
        }
        if(this.selectedMarker !== marker){
            if(this.selectedMarker){
                this.deselectSelectedMarker();
            }
            this.selectedMarker = marker;
            this.selectedMarker.applyCurrentZIndex();
        }
        if(withPopup){
            if(this.selectedMarker?.popupContent){
                this.openPopup();
            }
        }
        if(withMove){
            this.flyToFocus();
        } else if(withPopup){
            this.requirePopupInFocus();
        }
        if(this.props.onMarkerSelected){
            this.props.onMarkerSelected(marker);
        }
    }

    deselectSelectedMarker()
    {
        const previousSelectedMarker = this.selectedMarker;
        if(previousSelectedMarker){
            this.cluster?.deselectMarker(previousSelectedMarker).then()
        }
        this.selectedMarker = null;
        if(previousSelectedMarker){
            previousSelectedMarker.applyCurrentZIndex();
        }
        this.showSelectedMarkerPopup = false;
        this.popup?.closePopup();
    }

    openPopup()
    {
        if(this.popup){
            this.popup.closePopup();
        }
        if(!this.selectedMarker){
            return;
        }
        this.popup = new OSM.MapPopup(this, {
            ...this.coreConfig.popup,
            ...this.props.popup,
            ...this.selectedMarker?.popupProps,
            popupOptions: {
                ...this.coreConfig.popup?.popupOptions,
                ...this.props.popup?.popupOptions,
                ...this.selectedMarker?.popupProps?.popupOptions,
            }
        });
        this.popup.showPopup(true);
    }

    /**
     *
     * @returns {void}
     */
    closePopup()
    {
        this.popup?.closePopup();
    }
}
